// FocusView — the three Focus routes' shared wiring: the navigation callbacks
// `FocusPage` takes, once. Each route supplies its params:
//   /focus                                      → project picker
//   /focus/projects/$projectId                  → per-project wizard
//   /focus/projects/$projectId/issues/$iid      → refine chat for that issue
//
// Its own module, not `router.tsx`, so the Focus panel (`src/panel/`, the
// second Vite entry) builds the same three routes on its own router without
// importing the web's: that file makes the web's browser-history router at
// module scope and imports every other page (`test/panel-isolation.test.ts`).
//
// "Open the issue": the web opens the dashboard's issue page; the panel has
// no dashboard, so it opens the issue on GitLab through the host's link box.
// A GitHub repository (negative id) has no classic issue page on either: its
// issue opens in Focus.

import { useNavigate } from "@tanstack/react-router";

import { IssueDetailResponseSchema } from "@loom/shared/dashboard";
import { forgeOfProjectId } from "@loom/shared/forge";

import { httpGet } from "../lib/http";
import { isPanelHost, openExternal, openLoomPage } from "../lib/host";

import { FocusPage } from "./FocusPage";

/** The panel's "open the issue": its GitLab page, read from the issue detail route. */
const openIssueFromPanel = (projectId: number, iid: number): void => {
  void httpGet(`/api/projects/${projectId}/issues/${iid}`, IssueDetailResponseSchema).then(
    (detail) => openExternal(detail.issue.webUrl),
    // The URL could not be read: the web's own issue page, in the browser.
    () => openLoomPage(`/projects/${projectId}/issues/${iid}`),
  );
};

export function FocusView({
  projectId,
  refineIid,
  launchIssueIid,
}: {
  readonly projectId: number | null;
  readonly refineIid: number | null;
  /** The panel only: the issue its launch named, preselected in the search step. */
  readonly launchIssueIid?: number | null;
}) {
  const navigate = useNavigate();
  return (
    <FocusPage
      projectId={projectId}
      refineIid={refineIid}
      {...(launchIssueIid !== undefined && launchIssueIid !== null ? { launchIssueIid } : {})}
      onPickProject={(pid) =>
        navigate({
          to: "/focus/projects/$projectId",
          params: { projectId: String(pid) },
        })
      }
      onBackToProjects={() => navigate({ to: "/focus" })}
      onEnterRefine={(iid) =>
        navigate({
          to: "/focus/projects/$projectId/issues/$iid",
          params: { projectId: String(projectId), iid: String(iid) },
          replace: true,
        })
      }
      onLeaveRefine={() =>
        navigate({
          to: "/focus/projects/$projectId",
          params: { projectId: String(projectId) },
        })
      }
      onOpenIssue={(pid, iid) => {
        if (forgeOfProjectId(pid) === "github") {
          void navigate({
            to: "/focus/projects/$projectId/issues/$iid",
            params: { projectId: String(pid), iid: String(iid) },
          });
          return;
        }
        if (isPanelHost()) {
          openIssueFromPanel(pid, iid);
          return;
        }
        void navigate({
          to: "/projects/$projectId/issues/$iid",
          params: { projectId: String(pid), iid: String(iid) },
        });
      }}
    />
  );
}
